'use client'

import clsx from 'clsx'
import Link from 'next/link'
import type { MouseEvent, ReactNode } from 'react'

import { Stars } from '@/features/product-reviews/ui/stars/Stars'

import { PRODUCT_URL } from '@/shared/lib/const/env'
import { getWordEnding } from '@/shared/lib/utils/string/get-word-ending'

import cl from './ProductRating.module.scss'

export const OPEN_PRODUCT_REVIEWS_EVENT = 'openProductReviews'

type ProductRatingProps = {
	className?: string
	productCode?: string
	summary?: { RATING: number; COUNT: number }
	isMobileCompact?: boolean
	disableNavigation?: boolean
	showAverage?: boolean
	showEmptyReviewsText?: boolean
	showEmptyReviewsStars?: boolean
	underlineReviewsText?: boolean
}

export const ProductRating = ({
	className,
	productCode,
	summary,
	isMobileCompact = true,
	disableNavigation = false,
	showAverage = true,
	showEmptyReviewsText = false,
	showEmptyReviewsStars = false,
	underlineReviewsText = false
}: ProductRatingProps) => {
	const openReviews = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation()

		if (disableNavigation) {
			event.preventDefault()
			window.dispatchEvent(new CustomEvent(OPEN_PRODUCT_REVIEWS_EVENT))
		}
	}

	const renderReviewsAnchor = (content: ReactNode, ariaLabel: string, extraClassName?: string) => {
		if (disableNavigation) {
			return (
				<button
					type="button"
					className={clsx(cl.root, cl.button, extraClassName, className)}
					aria-label={ariaLabel}
					onClick={openReviews}>
					{content}
				</button>
			)
		}

		if (!productCode) return null

		return (
			<Link
				href={`${PRODUCT_URL}${productCode}?tab=reviews`}
				className={clsx(cl.root, extraClassName, className)}
				aria-label={ariaLabel}
				onClick={openReviews}
				prefetch={false}>
				{content}
			</Link>
		)
	}

	if (!summary || summary.COUNT <= 0) {
		if (!showEmptyReviewsText) return null

		const emptyReviewsContent = (
			<>
				{showEmptyReviewsStars && <Stars rating={0} size="catalog" mobileCompact={isMobileCompact} />}
				<span className={cl.emptyReviewsText}>Нет отзывов</span>
			</>
		)

		if (showEmptyReviewsStars && (disableNavigation || productCode)) {
			return renderReviewsAnchor(emptyReviewsContent, 'Перейти к отзывам', cl.emptyReviews)
		}

		return <span className={clsx(cl.root, cl.emptyReviews, className)}>{emptyReviewsContent}</span>
	}

	const rating = Math.max(0, Math.min(5, summary.RATING))
	const formattedRating = rating.toFixed(1)
	const reviewsContent = (
		<>
			<span className={cl.ratingValue}>
				<Stars rating={rating} size="catalog" mobileCompact={isMobileCompact} />
				{showAverage && <span className={cl.average}>{formattedRating}</span>}
			</span>
			<span className={clsx(cl.reviews, underlineReviewsText && cl.reviewsUnderlined)}>
				{summary.COUNT} {getWordEnding('отзыв', summary.COUNT, ['', 'а', 'ов'])}
			</span>
		</>
	)

	return renderReviewsAnchor(reviewsContent, `Рейтинг ${formattedRating} из 5`)
}
